import { useContext } from 'react'

import { TokenContext } from '../../../layouts/context'
import { useCartContext } from './useCartContext'

/**
 * Access token for mutations that target the active cart, or `undefined` when that cart is not the
 * customer's.
 *
 * Magento's `GetCartForUser` rejects a customer token against a guest quote, which is reachable
 * between login and the cart merge: the active cart is still the guest one while the shopper is
 * already authenticated. Sending the token in that window asks Magento to operate on a
 * `customer_id = 0` quote as the customer, and it answers `The current user cannot perform
 * operations on cart "…"`.
 *
 * This reads `type` as of now and does not wait for the cart to settle the way `cartMutationTarget$`
 * does, so it narrows that window rather than closing it: `type` flips to `customer` at login, and a
 * mutation issued before the merge finishes can still land on the guest quote. The recovery in
 * `useGuestCartGet` is what covers the remainder.
 *
 * Mirrors the pairing `useCartMutation` applies internally, for the call sites that issue cart
 * mutations directly. Requests that are customer-scoped rather than cart-scoped - the customer
 * address book, say - want `TokenContext` itself instead.
 */
export const useCartMutationToken = (): string | undefined => {
  const { type } = useCartContext()
  const token = useContext(TokenContext)
  return type === 'customer' ? token : undefined
}
